import { tWithKeyAttr as t } from "../../../i18nHelper";
import React, { useEffect, useState } from "react";
import styles from "./dashboard.module.css";
import { IQuestionnaireData } from "../../../services/types/utils.types";
import DashboardSelect from "../dashboardSelect/dashboardSelect";
import {
  useLazyGetAllAnswersQuery,
  useLazyGetAllCompaniesByCompanyIdQuery,
  useLazyGetAllQuestionnairesByCompanyIdQuery,
  useLazyGetAllUsersInCompanyQuery,
  useLazyGetOnlyQuestionsWithOptionsByQuestionnaireIdQuery,
  useUpdateQuestionsAndOptionsPreferencesMutation,
} from "../../../services/api/dashboards.api";
import {
  TCompaniesList,
  TUsersForComboType,
} from "../../../services/types/auth.types";
import { useAppSelector } from "../../../services/hooks";
import { adminSelector } from "../../../services/selectors/user.selectors";
import {
  TQuestionData,
  TGetAllQuestionsAndOptionsResponse,
} from "../../../services/types/user.types";
import QuestionChecklist from "../questionChecklist/questionChecklist";
import Loader from "../../loader/loader";
import Chart from "../chart/chart";
import DashboardCheckboxGroup from "../dashboardCheckBox/dashboardCheckBox";
import list from "../../../images/list.svg";
import CalendarDropdown from "../CalendarDropdown/CalendarDropdown";
import { BottomColorPalette } from "../../../utils/constants";
import ChartPopup from "../chartPopup/chartPopup";
import BackArrowButton from "../../backArrowButton/backArrowButton";
import i18n from "../../../i18n";
import ExportTableButtons from "../../exportTableButtons/ExportTableButtons";
import { formatPercent } from "../../../utils/constants";
import { useMemo } from "react";
function Dashboard({
  usersId,
  onClose,
}: {
  usersId?: number;
  onClose?: () => void;
}) {
  const [companiesComboData, setCompaniesComboData] = useState<
    TCompaniesList[] | undefined
  >(undefined);
  const [questionnairesCombosData, setQuestionnairesCombosData] = useState<
    IQuestionnaireData[] | undefined
  >(undefined);
  const [usersList, setUsersList] = useState<TUsersForComboType[] | undefined>(
    undefined
  );
  const [usersSelectedList, setUsersSelectedList] = useState<number[]>(
    usersId ? [usersId] : []
  );
  const [popupChart, setPopupChart] = useState<TQuestionData | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number>(0);


  const [selectedQuestionnairId, setSelectedQuestionnairId] =
    useState<number>(0);
  const [questionsWithOptions, setQuestionsWithOptions] =
    useState<TGetAllQuestionsAndOptionsResponse | null>(null);
  const [questionsAnalyzes, setQuestionsAnalyzes] = useState<
    TQuestionData[] | null
  >(null);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [openQuestionCheckList, setOpenQuestionCheckList] =
    useState<boolean>(true);
  const [selectedQuestions, setSelectedQuestions] = useState<
    Record<string, number[]>
  >({});
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResearch, setIsResearch] = useState(false);
  const [includeAnonymous, setIncludeAnonymous] = useState<boolean>(false);
  // console.log(" is research: " , isResearch);

  const admin = useAppSelector(adminSelector);

  const [getAllCompanies] = useLazyGetAllCompaniesByCompanyIdQuery();
  const [getAllUsers] = useLazyGetAllUsersInCompanyQuery();
  const [getQuestionnaires] = useLazyGetAllQuestionnairesByCompanyIdQuery();
  const [getQuestionnaireById, { isLoading: isQuestionnaireLoading }] =
    useLazyGetOnlyQuestionsWithOptionsByQuestionnaireIdQuery();
  const [getAllAnswers, { isLoading: isGetAllAnswersLoading }] =
    useLazyGetAllAnswersQuery();
  const [updateQuestionsAndOptionsPreferences, { isLoading: isUpdateLoading }] =
    useUpdateQuestionsAndOptionsPreferencesMutation();


  useEffect(() => {
    setSelectedCompanyId(admin.companyId);
    getAllCompanies().then((res) => {
      if ("data" in res) {
        setCompaniesComboData(res.data);
      }
    });
    getQuestionnaires().then((res) => {
      if ("data" in res) {
        setQuestionnairesCombosData(res.data);
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin, getAllCompanies, getQuestionnaires, getAllUsers]);

  useEffect(() => {
    if (popupChart) {
      const chartData = questionsAnalyzes?.find(
        (item) => item.questionsId === popupChart.questionsId
      );
      if (chartData) {
        setPopupChart(chartData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionsAnalyzes]);

  useEffect(() => {
    if (selectedQuestionnairId !== 0) {
      setQuestionsWithOptions(null);
      getQuestionnaireById(Number(selectedQuestionnairId)).then((res) => {
        if ("data" in res && res.data) {
          setQuestionsWithOptions(res.data);

          const currentQuestionsIds = new Set([
            ...res.data.questionsOptions.map((item) => item.Questions.Id),
            ...res.data.questionsEmotions.map((item) => item.Questions.Id),
          ]);

          // Build all-selected result as fallback
          const allResult: Record<number, number[]> = {};
          res.data.questionsOptions.forEach((item) => {
            allResult[item.Questions.Id] = item.QuestionsOptions.map(
              (option) => option.Id
            );
          });
          res.data.questionsEmotions.forEach((item) => {
            allResult[item.Questions.Id] = item.QuestionsEmotions.map(
              (option) => option.Id
            );
          });

          // If saved preferences exist for this questionnaire's questions, use them;
          // otherwise auto-select all
          const savedResult: Record<number, number[]> = {};
          for (const key in selectedQuestions) {
            const questionsId = Number(key);
            if (currentQuestionsIds.has(questionsId)) {
              savedResult[questionsId] = selectedQuestions[questionsId];
            }
          }

          setSelectedQuestions(
            Object.keys(savedResult).length > 0 ? savedResult : allResult
          );
          setOpenQuestionCheckList(true);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedQuestionnairId]);

  useEffect(() => {
    getAllUsers(selectedCompanyId ?? null).then((res) => {
      if ("data" in res) {
        setUsersList(res.data);
        const newUserIds = res.data?.map((user) => user.id) ?? [];
        if (usersId != null && newUserIds.includes(usersId)) {
          setUsersSelectedList([usersId]);
        } else {
          setUsersSelectedList(newUserIds);
        }
        setHasUserInteracted(true);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId]);

  useEffect(() => {
    if (!usersId) {
      getAllAnswers().then((response) => {
        if ("data" in response && response.data) {
          setQuestionsAnalyzes(response.data.questionsAnalyzes);
          setTotalUsers(response.data.totalUsers);
          (response.data?.companies[0]?.id ?? -1) !== selectedCompanyId &&
            setSelectedCompanyId(response.data?.companies[0]?.id ?? -1);
          (response.data?.questionnaireId ?? 0) !== selectedQuestionnairId &&
            setSelectedQuestionnairId(response.data?.questionnaireId ?? 0);
          setStartDate(
            response.data.startDate
              ? new Date(response.data.startDate)
              : undefined
          );
          if (!response.data.isResearch) {
            response.data.users?.length &&
              setUsersSelectedList(response.data.users);
          }
          setIsResearch(response.data.isResearch ?? false);
          setIncludeAnonymous(response.data.includeAnonymous ?? false);
          setEndDate(
            response.data.endDate ? new Date(response.data.endDate) : undefined
          );
          const allSelectedOptions = response.data.questionsAnalyzes.reduce<
            Record<number, number[]>
          >((acc, item) => {
            acc[item.questionsId] = item.options.map((sub) => sub.optionId);
            return acc;
          }, {});
          setSelectedQuestions(allSelectedOptions);
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersId, getAllAnswers, admin.companyId]);

  const getAllQuestions = (_selectedQuestions?: Record<string, number[]>) => {
    const selections = _selectedQuestions || selectedQuestions;
    setIsSubmitting(true);
    var objToBody = {};
    objToBody = { ...objToBody, selections: selections };
    if (!isResearch && usersSelectedList && usersSelectedList.length !== 0) {
      objToBody = {
        ...objToBody,
        users: usersSelectedList,
      };
    }
    // includeAnonymous is NOT sent to updateQuestionsAndOptionsPreferences
    // It will be used only when fetching data via getAllAnswers
    if (startDate && endDate) {
      const formatDate = (date: Date) =>
        date.getFullYear() +
        "-" +
        String(date.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(date.getDate()).padStart(2, "0");
      objToBody = {
        ...objToBody,
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
      };
    }
    const body = { ...objToBody, selections: selections };
    updateQuestionsAndOptionsPreferences({
      body: body,
      questionnairId: selectedQuestionnairId,
      companyId: selectedCompanyId,
    }).then((res) => {
      if ("data" in res) {

        getAllAnswers(includeAnonymous ? { includeAnonymous } : undefined).then((response) => {
          if ("data" in response && response.data) {

            setQuestionsAnalyzes(response.data.questionsAnalyzes);
            setTotalUsers(response.data.totalUsers);
            (response.data?.companies[0]?.id ?? -1) !== selectedCompanyId &&
              setSelectedCompanyId(response.data?.companies[0]?.id ?? -1);
            (response.data?.questionnaireId ?? 0) !== selectedQuestionnairId &&
              setSelectedQuestionnairId(response.data?.questionnaireId ?? 0);
            setStartDate(
              response.data.startDate
                ? new Date(response.data.startDate)
                : undefined
            );
            if (!response.data.isResearch) {
              response.data.users?.length &&
                setUsersSelectedList(response.data.users);
            }
            setIsResearch(response.data.isResearch ?? false);
            setIncludeAnonymous(response.data.includeAnonymous ?? false);
            setEndDate(
              response.data.endDate
                ? new Date(response.data.endDate)
                : undefined
            );
            setOpenQuestionCheckList(false);
            setIsDirty(false);
            setIsSubmitting(false);
          }
        });
      }
    }).finally(() => {
      setIsSubmitting(false);
    });
  };

  useEffect(() => {
    if (
      selectedCompanyId &&
      selectedQuestionnairId &&
      ((startDate && endDate) || (!startDate && !endDate))
    ) {
      // getAllQuestions();
      setHasUserInteracted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // selectedCompanyId,
    usersList,
    selectedQuestionnairId,
    // startDate,
    endDate,
    usersSelectedList,
    // selectedQuestions,
  ]);

  const exportData = useMemo(() => {
    return questionsAnalyzes?.flatMap(q =>
      q.options.map(o => ({
        Question: q.desc,
        Option: o.desc,
        Count: o.answersCount,
        Percentage: formatPercent(o.answerPercentage)
      }))
    ) ?? [];
  }, [questionsAnalyzes]);

  return (
    <div className={styles.container} dir={i18n.dir()}>
      <BackArrowButton
        text={t("PREVIOUS_SCREEN")}
        onClick={onClose}
        position={"center"}
      />
      <div className={styles.headerRow}>
        <h1 className={styles.title}>{t("STATISTICAL_REPORTS_FOR_RESEARCH")}</h1>
        {questionsAnalyzes && questionsAnalyzes.length > 0 && (
          <div className={styles.headerActions}>
            <ExportTableButtons
              entity="QuestionnaireDashboard"
              dataRows={exportData}
              title={`${t("STATISTICAL_REPORTS_FOR_RESEARCH")} - ${questionnairesCombosData?.find(q => q.id === Number(selectedQuestionnairId))?.desc ?? ""}`}
            />
          </div>
        )}
      </div>
      <div className={styles.comboBoxContainer}>
        <div className={styles.comboBoxWrapper}>
          {companiesComboData && companiesComboData.length !== 0 && (
            <DashboardSelect
              title={t("COMPANY_NAME_2")}
              options={companiesComboData.map((c) => {
                const currentLang = i18n.language || "he";
                let label = c.Name; // Default to Name

                if (currentLang !== "he") {
                  label = c.NameEn || c.Name;
                }

                return {
                  value: c.Id.toString(),
                  label,
                };
              })}
              onChange={(val) => {
                                
                setSelectedCompanyId(val);
                // setHasUserInteracted(true);
              }}
              defaultOption={selectedCompanyId}
            />
          )}

          <DashboardSelect
            title={t("REPORT_TYPE")}
            options={
              questionnairesCombosData?.map((q) => ({
                value: q.id.toString(),
                label: q.desc,
              })) ?? null
            }
            onChange={(val) => {
              setSelectedQuestionnairId(val);
              setHasUserInteracted(true);
              setIsDirty(true);
              setIsSidebarOpen(true);
            }}
            defaultOption={selectedQuestionnairId}
          />

          <CalendarDropdown
            className={styles.comboBoxTitle}
            title={t("REPORT_PERIOD")}
            startDate={startDate}
            endDate={endDate}
            setStartDate={(date) => {
              setStartDate(date);
              setHasUserInteracted(true);
              setIsDirty(true);
            }}
            setEndDate={(date) => {
              setEndDate(date);
              setHasUserInteracted(true);
              setIsDirty(true);
            }}
          />

          {!isResearch && usersList && (
            <div className={styles.usersSection}>
              <DashboardCheckboxGroup
                title={t("PATIENTS_LIST")}
                options={usersList.map((op) => ({
                  value: op.id.toString(),
                  label: op.id.toString(),
                }))}
                onChange={(vals) => {
                  setUsersSelectedList(vals);
                  setHasUserInteracted(true);
                  setIsDirty(true);
                }}
                selectedValues={usersSelectedList}
              />
              <div className={styles.checkboxContainer}>
                <span className={styles.tooltip}>
                  {t("ANONYMOUS_RESPONSES_HINT")}
                </span>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={includeAnonymous}
                    onChange={(e) => {
                      setIncludeAnonymous(e.target.checked);
                      setHasUserInteracted(true);
                      setIsDirty(true);
                    }}
                    className={styles.toggleInput}
                  />
                  <div className={styles.toggleSwitch}>
                    <div className={styles.toggleSlider}></div>
                  </div>
                  <span>+ Anonymous</span>
                </label>
              </div>
            </div>
          )}
          {isResearch && (
            <p className={styles.researchModeHint} role="status">
              {t("PATIENT_LIST_HIDDEN_RESEARCH")}
            </p>
          )}
          <button
            className={`${styles.submitContainer} ${isDirty ? styles.submitDirty : ''}`}
            onClick={() => {
              setHasUserInteracted(true);
              setIsSubmitting(true);
              getAllQuestions();
            }}
            disabled={isSubmitting || selectedQuestionnairId === 0 || Object.keys(selectedQuestions).length === 0}
          >
            {isSubmitting ? (
              <span className={styles.submitSpinner}></span>
            ) : null}
            <span className={styles.submitText}>{t("SEND")}</span>
          </button>
        </div>
      </div>
      {isGetAllAnswersLoading || isUpdateLoading || isSubmitting ? (
        <Loader />
      ) : (
        <>
          {isDirty && questionsAnalyzes && questionsAnalyzes.length > 0 && (
            <div className={styles.dirtyBanner}>
              <span className={styles.dirtyIcon}>⚠️</span>
              <span>{t("RESULTS_OUTDATED_CLICK_SUBMIT")}</span>
            </div>
          )}

          {questionnairesCombosData &&
            questionsWithOptions &&
            questionsWithOptions.questionsOptions.length !== 0 &&
            !isQuestionnaireLoading && (
              <button
                className={styles.toggleSidebarBtn}
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              >
                <img src={list} alt={t("DISPLAY_QUESTIONS")} />
                <span>{t("CONFIGURE_QUESTIONS")}</span>
              </button>
            )}

          {isSidebarOpen && questionsWithOptions && (
            <>
              <div className={styles.sidebarBackdrop} onClick={() => setIsSidebarOpen(false)} />
              <QuestionChecklist
                title={
                  questionnairesCombosData?.find(
                    (item) => item.id === Number(selectedQuestionnairId)
                  )?.desc ?? ""
                }
                questionsWithOptions={questionsWithOptions}
                usersSelectedList={usersSelectedList}
                setSelectedQuestions={(vals) => {
                  setSelectedQuestions(vals);
                  setIsDirty(true);
                }}
                selectedQuestions={selectedQuestions}
                onClose={() => setIsSidebarOpen(false)}
                onApply={() => {
                  setHasUserInteracted(true);
                  setIsSubmitting(true);
                  getAllQuestions();
                }}
                isLoading={isQuestionnaireLoading}
              />
            </>
          )}

          <div className={`${styles.chartsWrapper} ${isDirty ? styles.chartsStale : ''}`}>
            {isDirty && questionsAnalyzes && questionsAnalyzes.length > 0 && (
              <div className={styles.staleOverlay}>
                <div className={styles.staleBadge}>{t("OUTDATED")}</div>
              </div>
            )}
            <div className={styles.charts}>
              {questionsAnalyzes && questionsAnalyzes.length !== 0 ? (
                // questionsAnalyzes.every(question =>
                //     question.options.every(option => option.answersCount === 0)
                // ) ? (
                //     <p>{t('NO_REPORTS_TO_DISPLAY')}</p>
                // ) : (
                questionsAnalyzes.map((question, index) => {
                  const isGroupQuestion =
                    question.options.some(
                      (item, idx, arr) =>
                        arr.findIndex((x) => x.desc === item.desc) !== idx &&
                        item.group?.groupId
                    ) || question.isEmotion;

                  return (
                    <div
                      className={styles.chartContainer}
                      key={index}
                      onClick={() => {
                        if (!isGroupQuestion) {
                          setPopupChart(question);
                        }
                      }}
                    >
                      <Chart
                        key={question.questionsId}
                        total={totalUsers}
                        data={question}
                        setDataToAnalyze={(data: TQuestionData[]) =>
                          setQuestionsAnalyzes(data)
                        }
                        color={
                          BottomColorPalette[index % BottomColorPalette.length]
                        }
                        onDiagramTypeChange={() => {
                          // Refresh data with current filters after diagram type change
                          getAllAnswers(includeAnonymous ? { includeAnonymous } : undefined).then((response) => {
                            if ("data" in response && response.data) {
                              setQuestionsAnalyzes(response.data.questionsAnalyzes);
                              setTotalUsers(response.data.totalUsers);
                            }
                          });
                        }}
                      />
                    </div>
                  );
                })
              ) : // )
                selectedQuestionnairId === 0 ? (
                  <p className="note">{t("PLEASE_SELECT_REPORT_TYPE")}</p>
                ) : (
                  <p className="note">{t("NO_DATA_TO_DISPLAY")}</p>
                )}
              {popupChart && (
                <ChartPopup
                  data={popupChart}
                  total={totalUsers}
                  color={BottomColorPalette[0]}
                  setDataToAnalyze={(data: TQuestionData[]) =>
                    setQuestionsAnalyzes(data)
                  }
                  onClose={() => setPopupChart(null)}
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Dashboard;
